import { lazy, type ComponentType } from 'react';

/**
 * Erreur de chargement d'un chunk dynamique (import()) — typiquement un déploiement
 * a fait tourner les hashes : l'onglet ouvert référence un fichier .js qui n'existe
 * plus → le serveur renvoie index.html (fallback SPA) → « 'text/html' is not a valid
 * JavaScript MIME type » / « Failed to fetch dynamically imported module ».
 */
function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as any)?.message || err || '');
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|not a valid JavaScript MIME type|Importing a module script failed|ChunkLoadError|Loading chunk .* failed|Unable to preload CSS/i.test(msg);
}

const RELOAD_KEY = 'tc:last-chunk-reload';
const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Comme `React.lazy`, mais sur erreur de chunk périmé (post-déploiement) recharge la
 * page UNE fois pour récupérer les assets frais, au lieu de crasher vers l'ErrorBoundary.
 * Garde anti-boucle : pas de second reload dans les 10 s (si le chunk est réellement
 * cassé, on relance l'erreur → ErrorBoundary).
 */
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (isChunkLoadError(err)) {
        const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
        if (Date.now() - last > RELOAD_COOLDOWN_MS) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
          return new Promise<{ default: T }>(() => {}); // suspend jusqu'au reload
        }
      }
      throw err;
    }),
  );
}
